import {
  useRef,
  useEffect,
  useState,
  forwardRef,
  ForwardRefRenderFunction,
  useImperativeHandle,
  useContext,
  useCallback
} from 'react'

import * as LivekitClient from 'livekit-client'
import get from 'lodash/get'
import jsLogger from 'js-logger'
import { serializeError } from 'shared/utils/serializeError'
import UserClickContext from 'shared/contexts/UserClickContext'
import { AvatarSessionBootstrap } from 'types/back'

// Route LiveKit internal logs through jsLogger
LivekitClient.setLogLevel('debug')
LivekitClient.setLogExtension((level, msg, context) => {
  const ctx =
    typeof context === 'object' && context !== null ? context : { context }
  switch (level) {
    case LivekitClient.LogLevel.trace:
    case LivekitClient.LogLevel.debug:
      jsLogger.debug(`[LiveKit] ${msg}`, ctx)
      break
    case LivekitClient.LogLevel.info:
      jsLogger.info(`[LiveKit] ${msg}`, ctx)
      break
    case LivekitClient.LogLevel.warn:
      jsLogger.warn(`[LiveKit] ${msg}`, ctx)
      break
    case LivekitClient.LogLevel.error:
      jsLogger.error(`[LiveKit] ${msg}`, ctx)
      break
  }
})

// The room + avatar media are provisioned by the socket server (it mints the
// LiveKit creds, starts the LiveAvatar LITE session, and drives all actuation
// over the LITE control WS). This component is a THIN client: it connects to the
// server-provisioned room, renders whatever avatar video/audio tracks arrive,
// publishes the user's camera + mic into the room (for server-side Egress
// recording + frame analysis), and nothing else. Turn-taking, speech, barge-in
// and session lifecycle are all owned by the server and relayed to the app via
// the socket (serverPhrase / sessionFinished) — NOT by this component.
// Provider-agnostic on purpose: the room's avatar may be HeyGen today and
// something else tomorrow; the client only plays the tracks.

type SessionInfo = {
  session_id: string
  livekit_url: string
  livekit_client_token: string
}

// --- A/V sync diagnostics ------------------------------------------------
// We see episodic "audio behind video" drift that we could only reproduce
// under network throttling, yet it has also been observed on a good network.
// To catch a real incident, we sample inbound-rtp stats for the avatar's
// audio and video tracks once a second for the whole session and log the
// rendered A/V offset, jitter-buffer depth and NetEQ stretch/conceal/freeze
// counters. This runs on EVERY session by default (so a production lip-sync
// report is diagnosable from logs); opt out via ?avSyncDebug=0 or localStorage
// avSyncDebug=0 if log volume ever becomes a problem.
const AV_SYNC_SAMPLE_INTERVAL_MS = 1000
// Sample for the whole session (auto-stops at endSession). The good-network
// drift can appear on a late phrase, so a 60s window would miss it; the cap is
// just a safety backstop against a runaway timer.
const AV_SYNC_MAX_SAMPLES = 600

// Opt-IN: the per-second A/V-sync sampler emits ~1 log line/second for the whole
// session, so it stays off unless explicitly requested via ?avSyncDebug=1 (or
// localStorage avSyncDebug=1). Lip-sync is stable; enable this only to diagnose a
// reported drift. The audio-prime fix (avSyncPrimeEnabled) is separate and stays
// on — it's a real fix, not logging.
const avSyncSamplingEnabled = (): boolean => {
  try {
    const raw =
      new URLSearchParams(window.location.search).get('avSyncDebug') ??
      window.localStorage.getItem('avSyncDebug')
    return raw === '1' || raw === 'true'
  } catch {
    return false
  }
}

// Experiment: force an equal playout delay on both avatar tracks so the
// receiver aligns them instead of letting the audio jitter buffer run ~0.25s
// deeper than video under a degraded network (the measured cause of the
// "audio behind video" drift). Enabled via ?avSyncFix=<seconds> or
// localStorage avSyncFix=<seconds>; "1"/"true" maps to the default below.
// Returns undefined when disabled. Remove once a value is chosen for good.
const AV_SYNC_DEFAULT_PLAYOUT_DELAY_SEC = 0.3

const avSyncPlayoutDelaySec = (): number | undefined => {
  try {
    const raw =
      new URLSearchParams(window.location.search).get('avSyncFix') ??
      window.localStorage.getItem('avSyncFix')
    if (raw === null) return undefined
    if (raw === '1' || raw === 'true') return AV_SYNC_DEFAULT_PLAYOUT_DELAY_SEC
    const parsed = Number(raw)
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined
  } catch {
    return undefined
  }
}

// Fix: "prime" the avatar audio track playout before the user-gesture click.
// We connect/subscribe early (so the avatar speaks right after the click) but
// `playVideo` only sets the <video> srcObject AFTER the click — so during the
// pre-click window the audio track has no consumer, the WebRTC receiver can't
// drain it, and the jitter buffer backs up (measured: a 6s gap → ~4.8s buffer,
// audible "audio behind video" on the first phrase; severity scales with the
// gap). Attaching the track to a hidden, muted, autoplay <audio> (muted
// autoplay needs no gesture) gives the receiver a consumer immediately, so the
// buffer stays shallow and playout is in sync at click time. Default on; opt
// out with ?avSyncPrime=0 to A/B against the un-primed buffer.
const avSyncPrimeEnabled = (): boolean => {
  try {
    const raw =
      new URLSearchParams(window.location.search).get('avSyncPrime') ??
      window.localStorage.getItem('avSyncPrime')
    return raw !== '0' && raw !== 'false'
  } catch {
    return true
  }
}

// Debug: artificially defer the first video playout by N seconds after the
// tracks are ready, to deterministically reproduce a long "pre-click" window
// (the gap between track subscription and playout that lets the audio jitter
// buffer back up). With ?avSyncPrime=0 the buffer should balloon; with priming
// on it should stay shallow. Off (0) unless ?avSyncDelayPlay=<seconds> is set.
const avSyncDelayPlayMs = (): number => {
  try {
    const raw =
      new URLSearchParams(window.location.search).get('avSyncDelayPlay') ??
      window.localStorage.getItem('avSyncDelayPlay')
    if (raw === null) return 0
    const parsed = Number(raw)
    return Number.isFinite(parsed) && parsed > 0 ? parsed * 1000 : 0
  } catch {
    return 0
  }
}

// Pull the single inbound-rtp report out of a track's stats. lodash/get keeps
// us safe against partial stat objects across browsers.
const readInboundRtp = async (
  track: LivekitClient.RemoteTrack | null
): Promise<Record<string, unknown> | undefined> => {
  if (!track) return undefined
  const report = await track.getRTCStatsReport()
  if (!report) return undefined
  let inbound: Record<string, unknown> | undefined
  report.forEach((stat) => {
    if (get(stat, 'type') === 'inbound-rtp') {
      inbound = stat as Record<string, unknown>
    }
  })
  return inbound
}

// Average a cumulative jitter-buffer field (e.g. jitterBufferDelay,
// jitterBufferTargetDelay, jitterBufferMinimumDelay) in ms — per spec these are
// summed over jitterBufferEmittedCount, so the per-sample value is total/count.
// undefined when the browser hasn't reported it yet.
const avgBufferFieldMs = (
  stat: Record<string, unknown> | undefined,
  field: string
): number | undefined => {
  const total = get(stat, field)
  const emitted = get(stat, 'jitterBufferEmittedCount')
  if (typeof total !== 'number' || typeof emitted !== 'number' || emitted === 0)
    return undefined
  return Math.round((total / emitted) * 1000)
}

// Actual residence time in the jitter buffer (the delay the receiver is
// currently applying).
const avgJitterBufferMs = (
  stat: Record<string, unknown> | undefined
): number | undefined => avgBufferFieldMs(stat, 'jitterBufferDelay')

// Opus runs at 48kHz, so a sample count converts to ms by /48. NetEQ reports
// how many samples it inserted (time-stretching audio when starved) vs removed
// (compressing to catch up); the net of the two is the cumulative latency the
// audio playout has drifted relative to real time — our best proxy for a
// multi-second "audio behind video" lag that a jitter buffer can't explain.
const AUDIO_SAMPLES_PER_MS = 48
const samplesToMs = (samples: unknown): number | undefined =>
  typeof samples === 'number' ? Math.round(samples / AUDIO_SAMPLES_PER_MS) : undefined

export interface ILiveKitMediaPlane {
  stop: () => Promise<void>
}

type Props = {
  onSessionStarted: () => void
  handleVideoClick?: () => void
  // Server-minted LiveKit creds + room. The server has already started the
  // avatar session and the bot has joined this room server-side; we only
  // connect, subscribe and render.
  avatarSession: AvatarSessionBootstrap
  // The user's live camera+mic stream. Its video + audio tracks are published
  // into the room as adaptive simulcast so the server can subscribe to live
  // frames + Egress can record the composite. The latency-critical STT path
  // stays on the direct PCM socket, untouched.
  userStream?: MediaStream | null
  avatarImageUrl?: string
  onError?: (message: string) => void
  trackInitializationLatencies?: (
    full: number,
    streamingNew: number,
    connectionPreparing: number,
    websocket: number
  ) => void
  onConnectionStatusChanged?: (isConnected: boolean) => void
  onIdleTimeout?: () => void
}

const LiveKitMediaPlane: ForwardRefRenderFunction<ILiveKitMediaPlane, Props> = (
  {
    onSessionStarted,
    handleVideoClick,
    avatarSession,
    userStream,
    avatarImageUrl,
    onError,
    trackInitializationLatencies,
    onConnectionStatusChanged,
    onIdleTimeout
  },
  ref
) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream>()
  const sessionInfoRef = useRef<SessionInfo | undefined>()
  const roomRef = useRef<LivekitClient.Room | null>(null)
  // Guard so the user camera is published exactly once per room (room.connect
  // can resolve again after a reconnect).
  const cameraPublishedRef = useRef(false)
  // The exact MediaStreamTrack behind each publication, plus the publication
  // itself. The document scan stops the app stream's tracks — the very ones
  // handed to publishTrack — and hands back fresh ones on unmount, so these are
  // what distinguish a live publication from one left pointing at a dead track.
  const publishedCameraTrackRef = useRef<MediaStreamTrack | null>(null)
  const publishedMicTrackRef = useRef<MediaStreamTrack | null>(null)
  // Guard so the user microphone is published exactly once per room. The mic
  // goes into the room ONLY so the egress composite carries the user's voice
  // alongside the avatar's; the latency-critical STT path stays on the direct
  // PCM socket, untouched.
  const micPublishedRef = useRef(false)
  // Whether the LiveKit room is currently connected. State (not just a ref) so
  // the publish effect re-runs when connection flips — covers every ordering of
  // {room connected, camera stream ready}.
  const roomConnectedRef = useRef(false)
  const [roomConnected, setRoomConnected] = useState(false)
  const { clicked } = useContext(UserClickContext)
  const userClickedRef = useRef(clicked)
  const mountedAt = useRef(Date.now())
  const streamingNewLatency = useRef(0)
  const connectionPreparingLatency = useRef(0)
  const sessionStartedCalledRef = useRef(false)
  // A/V sync diagnostics: refs to the subscribed avatar tracks + sampler timer.
  const avSyncVideoTrackRef = useRef<LivekitClient.RemoteTrack | null>(null)
  const avSyncAudioTrackRef = useRef<LivekitClient.RemoteTrack | null>(null)
  const avSyncTimerRef = useRef<number | undefined>(undefined)
  const avSyncSamplesRef = useRef(0)
  // Hidden muted <audio> that primes avatar-audio playout before the click.
  const audioPrimeElRef = useRef<HTMLAudioElement | null>(null)
  // Debug: ensures the avSyncDelayPlay deferral fires only once.
  const avSyncPlayDeferredRef = useRef(false)
  // One-time markers: first audio packet / first decoded video frame seen, to
  // expose cold-start media-arrival delay.
  const firstAudioLoggedRef = useRef(false)
  const firstVideoLoggedRef = useRef(false)

  const stopAvSyncSampling = () => {
    if (avSyncTimerRef.current !== undefined) {
      window.clearInterval(avSyncTimerRef.current)
      avSyncTimerRef.current = undefined
      jsLogger.log('AVSYNC: sampling stopped')
    }
  }

  // Attach the avatar audio track to a hidden, muted, autoplay <audio> so the
  // WebRTC receiver keeps draining the audio jitter buffer during the pre-click
  // window (see avSyncPrimeEnabled). Idempotent.
  const startAudioPrime = (track: LivekitClient.RemoteTrack) => {
    if (!avSyncPrimeEnabled()) return
    if (audioPrimeElRef.current) return
    try {
      const el = document.createElement('audio')
      // muted, not volume=0: iOS Safari volume is read-only, muted is what makes
      // muted-autoplay work without a user gesture.
      el.muted = true
      el.volume = 0
      el.autoplay = true
      el.setAttribute('playsinline', 'true')
      el.setAttribute('aria-hidden', 'true')
      el.style.display = 'none'
      el.srcObject = new MediaStream([track.mediaStreamTrack])
      document.body.appendChild(el)
      audioPrimeElRef.current = el
      jsLogger.log('AVSYNC: audio prime sink attached', {
        tSinceMountMs: Date.now() - mountedAt.current
      })
      el.play()
        .then(() =>
          jsLogger.log('AVSYNC: audio prime sink playing', {
            tSinceMountMs: Date.now() - mountedAt.current
          })
        )
        .catch((e) =>
          jsLogger.warn('AVSYNC: audio prime sink play rejected', {
            error: e?.message
          })
        )
    } catch (error) {
      jsLogger.warn('AVSYNC: audio prime sink failed', {
        error: serializeError(error)
      })
    }
  }

  // Tear down the prime sink once the real <video> playout takes over (or on
  // session end). The single receiver NetEQ is then drained by the video
  // element, so the handoff is seamless.
  const stopAudioPrime = () => {
    const el = audioPrimeElRef.current
    if (!el) return
    try {
      el.pause()
      el.srcObject = null
      el.remove()
    } catch (error) {
      jsLogger.warn('AVSYNC: audio prime sink teardown failed', {
        error: serializeError(error)
      })
    }
    audioPrimeElRef.current = null
    jsLogger.log('AVSYNC: audio prime sink detached', {
      tSinceMountMs: Date.now() - mountedAt.current
    })
  }

  // Sample inbound-rtp stats for the avatar audio+video tracks every second.
  // Signals to read when a lip-sync incident shows up in logs:
  //  - avOffsetMs (audioPlayout - videoPlayout): direct A/V offset; negative =
  //    audio behind video. Often absent until RTCP sync is established.
  //  - audioNetStretchMs / audioConcealedMs / videoFreezeCount: under a bad
  //    link audio stretches+conceals (falls behind) while video freezes+skips
  //    (stays live) — the multi-second drift signature.
  //  - audioJitterBufferMs vs videoJitterBufferMs: steady-state buffer skew.
  const startAvSyncSampling = () => {
    if (!avSyncSamplingEnabled()) return
    if (avSyncTimerRef.current !== undefined) return
    avSyncSamplesRef.current = 0
    jsLogger.log('AVSYNC: sampling started', {
      audioPrimeEnabled: avSyncPrimeEnabled()
    })
    const tick = async () => {
      try {
        const [audio, video] = await Promise.all([
          readInboundRtp(avSyncAudioTrackRef.current),
          readInboundRtp(avSyncVideoTrackRef.current)
        ])
        const audioPlayout = get(audio, 'estimatedPlayoutTimestamp')
        const videoPlayout = get(video, 'estimatedPlayoutTimestamp')
        const avOffsetMs =
          typeof audioPlayout === 'number' && typeof videoPlayout === 'number'
            ? Math.round(audioPlayout - videoPlayout)
            : undefined
        const decelMs = samplesToMs(get(audio, 'insertedSamplesForDeceleration'))
        const accelMs = samplesToMs(get(audio, 'removedSamplesForAcceleration'))

        // One-time cold-start markers: when the first audio packet / video frame
        // actually arrives. A cold HeyGen/LiveKit backend delays these.
        const audioPkts = get(audio, 'packetsReceived')
        if (
          !firstAudioLoggedRef.current &&
          typeof audioPkts === 'number' &&
          audioPkts > 0
        ) {
          firstAudioLoggedRef.current = true
          jsLogger.log('AVSYNC: first audio packet', {
            tSinceMountMs: Date.now() - mountedAt.current
          })
        }
        const videoFrames = get(video, 'framesDecoded')
        if (
          !firstVideoLoggedRef.current &&
          typeof videoFrames === 'number' &&
          videoFrames > 0
        ) {
          firstVideoLoggedRef.current = true
          jsLogger.log('AVSYNC: first video frame', {
            tSinceMountMs: Date.now() - mountedAt.current
          })
        }

        jsLogger.log('AVSYNC sample', {
          tSinceMountMs: Date.now() - mountedAt.current,
          sample: avSyncSamplesRef.current,
          // negative => audio behind video (the reported lag)
          avOffsetMs,
          audioJitterBufferMs: avgJitterBufferMs(audio),
          videoJitterBufferMs: avgJitterBufferMs(video),
          // What NetEQ is *targeting* for the buffer.
          audioJitterTargetMs: avgBufferFieldMs(audio, 'jitterBufferTargetDelay'),
          audioJitterMinimumMs: avgBufferFieldMs(
            audio,
            'jitterBufferMinimumDelay'
          ),
          // The real signal: how far the actual buffer overfills its target at
          // stream start. Large positive at the start (then drained by NetEQ
          // acceleration) = the "audio behind video on the first phrase" lag.
          audioBufferOverfillMs: (() => {
            const buf = avgJitterBufferMs(audio)
            const tgt = avgBufferFieldMs(audio, 'jitterBufferTargetDelay')
            return typeof buf === 'number' && typeof tgt === 'number'
              ? buf - tgt
              : undefined
          })(),
          // true while the muted prime sink is still attached (pre-handoff).
          audioPrimeActive: !!audioPrimeElRef.current,
          // Cumulative NetEQ stretch: net (decel - accel) is the audio playout's
          // accumulated latency vs real time — the multi-second drift suspect.
          audioDecelMs: decelMs,
          audioAccelMs: accelMs,
          audioNetStretchMs:
            typeof decelMs === 'number' && typeof accelMs === 'number'
              ? decelMs - accelMs
              : undefined,
          audioConcealedMs: samplesToMs(get(audio, 'concealedSamples')),
          audioPacketsLost: get(audio, 'packetsLost'),
          videoFreezeCount: get(video, 'freezeCount'),
          videoTotalFreezesSec: get(video, 'totalFreezesDuration'),
          videoFramesDropped: get(video, 'framesDropped'),
          audioPacketsReceived: get(audio, 'packetsReceived'),
          videoFramesDecoded: get(video, 'framesDecoded'),
          audioJitterSec: get(audio, 'jitter'),
          videoJitterSec: get(video, 'jitter')
        })
      } catch (error) {
        jsLogger.warn('AVSYNC sample failed', { error: serializeError(error) })
      }
      avSyncSamplesRef.current += 1
      if (avSyncSamplesRef.current >= AV_SYNC_MAX_SAMPLES) {
        stopAvSyncSampling()
      }
    }
    avSyncTimerRef.current = window.setInterval(
      tick,
      AV_SYNC_SAMPLE_INTERVAL_MS
    )
    tick()
  }

  useImperativeHandle(ref, () => ({
    stop: endSession
  }))

  useEffect(() => {
    jsLogger.log('LiveKitMediaPlane clicked effect', { clicked })
    if (clicked) {
      userClickedRef.current = true
      playVideo()
    }
  }, [clicked])

  const playVideo = useCallback(() => {
    jsLogger.log('LiveKitMediaPlane: playVideo', { isClicked: clicked })
    const v = videoRef.current
    const stream = streamRef.current
    if (!userClickedRef.current) {
      jsLogger.log('LiveKitMediaPlane: playVideo, user has not interacted yet')
      return
    }
    if (stream && v) {
      // Debug: defer the first real playout to simulate a long pre-click
      // window. Deferred BEFORE attaching srcObject, so the audio track has no
      // consumer during the delay — faithfully reproducing the real gap where
      // the buffer backs up (unlike attaching first, which would drain it).
      const delayMs = avSyncDelayPlayMs()
      if (delayMs > 0 && !avSyncPlayDeferredRef.current) {
        avSyncPlayDeferredRef.current = true
        jsLogger.log('AVSYNC: deferring first playout (debug)', { delayMs })
        window.setTimeout(() => playVideo(), delayMs)
        return
      }

      if (v.srcObject !== stream) {
        v.srcObject = stream
      }

      // Only call play if video is paused or ended, and do not force another play if not needed
      if (v.paused || v.ended) {
        v.play()
          .then(() => {
            jsLogger.log('--> Video Play success')
            // The visible <video> now drives audio playout; drop the priming
            // sink so a single element renders the track.
            stopAudioPrime()
          })
          .catch((e) =>
            jsLogger.log('--> Video Play error', { error: e.message })
          )
      } else {
        jsLogger.log('Video already playing')
        stopAudioPrime()
      }
      if (!sessionStartedCalledRef.current) {
        sessionStartedCalledRef.current = true
        onSessionStarted()
      }
    } else {
      jsLogger.warn('playVideo stream or videoref is not ready')
    }
  }, [clicked, onSessionStarted])

  // Connect to the server-provisioned room and wire up media rendering.
  const createNewSession = useCallback(async () => {
    try {
      jsLogger.log('LiveKitMediaPlane: connecting to server-provisioned room', {
        room: avatarSession.roomName
      })
      const startTime = Date.now()

      // The server already minted the LiveKit room + LiveAvatar session and the
      // bot joined server-side; we only subscribe and render.
      const sessionInfo: SessionInfo = {
        session_id: avatarSession.roomName,
        livekit_url: avatarSession.livekitUrl,
        livekit_client_token: avatarSession.clientToken
      }
      streamingNewLatency.current = Date.now() - startTime
      sessionInfoRef.current = sessionInfo

      // Create LiveKit Room
      jsLogger.log('Creating LiveKit Room...')
      const room = new LivekitClient.Room({
        adaptiveStream: true,
        dynacast: true,
        disconnectOnPageLeave: false,
        videoCaptureDefaults: {
          resolution: LivekitClient.VideoPresets.h720.resolution
        },
        reconnectPolicy: {
          nextRetryDelayInMs: (ctx: LivekitClient.ReconnectContext) => {
            jsLogger.log('LiveKitMediaPlane: ReconnectContext', { ctx })
            if (ctx.retryCount > 8) {
              return null
            }
            onConnectionStatusChanged?.(false)
            return 2000
          }
        }
      })

      jsLogger.log('LiveKitMediaPlane: Room created')
      roomRef.current = room

      room.on(LivekitClient.RoomEvent.Connected, () => {
        jsLogger.log('LiveKitMediaPlane: Connected to room.')
        roomConnectedRef.current = true
        setRoomConnected(true)
        onConnectionStatusChanged?.(true)
      })

      room.on(LivekitClient.RoomEvent.Reconnecting, () => {
        jsLogger.warn('LiveKitMediaPlane: Reconnecting...')
        onConnectionStatusChanged?.(false)
      })

      room.on(LivekitClient.RoomEvent.Reconnected, () => {
        jsLogger.log('LiveKitMediaPlane: Reconnected successfully.')
        onConnectionStatusChanged?.(true)
      })

      room.on(LivekitClient.RoomEvent.Disconnected, (reason) => {
        jsLogger.log(`LiveKitMediaPlane: Disconnected (${reason})`)
        roomConnectedRef.current = false
        setRoomConnected(false)
        onConnectionStatusChanged?.(false)
        if (reason === 5 && onIdleTimeout) {
          jsLogger.log(
            'LiveKitMediaPlane: Disconnect reason is 5 and onIdleTimeout exists.'
          )
          onIdleTimeout()
        } else if (reason !== 1) {
          onError && onError('Network issue detected')
        }
      })

      // Handle media streams — render the avatar's video + audio tracks.
      const mediaStream = new MediaStream()

      room.on(
        LivekitClient.RoomEvent.TrackSubscribed,
        (track, publication, participant) => {
          if (track.kind !== 'video' && track.kind !== 'audio') return

          // LiveAvatar puts a second participant in the room — the
          // orchestration "agent" (identity `liveavatar-agent-*`) — that
          // publishes its own copy of the speech as `roomio_audio`, separate
          // from the avatar's lip-synced `avatar-audio` (from the `heygen`
          // participant). Subscribing to both makes the <video> play two
          // slightly-desynced voices (echo), live and in the recording. Keep
          // only the avatar's audio; the agent's video/data are unaffected.
          if (
            track.kind === 'audio' &&
            /^liveavatar-agent/.test(participant?.identity || '')
          ) {
            jsLogger.log('Skipping agent audio track (echo source)', {
              identity: participant?.identity,
              trackName: publication?.trackName
            })
            return
          }

          mediaStream.addTrack(track.mediaStreamTrack)
          // Keep references for A/V sync diagnostics sampling.
          if (track.kind === 'video') avSyncVideoTrackRef.current = track
          if (track.kind === 'audio') {
            avSyncAudioTrackRef.current = track
            // Prime audio playout now so the jitter buffer doesn't back up
            // during the pre-click window.
            startAudioPrime(track)
          }

          // Experiment: pin an equal playout delay on both tracks so the
          // receiver keeps them aligned under network jitter.
          const playoutDelay = avSyncPlayoutDelaySec()
          if (playoutDelay !== undefined) {
            try {
              track.setPlayoutDelay(playoutDelay)
              jsLogger.log('AVSYNC: setPlayoutDelay applied', {
                kind: track.kind,
                playoutDelay,
                readback: track.getPlayoutDelay()
              })
            } catch (error) {
              jsLogger.warn('AVSYNC: setPlayoutDelay failed', {
                kind: track.kind,
                error: serializeError(error)
              })
            }
          }
          if (
            mediaStream.getVideoTracks().length > 0 &&
            mediaStream.getAudioTracks().length > 0
          ) {
            jsLogger.log('Media stream ready')
            trackInitializationLatencies &&
              trackInitializationLatencies(
                Date.now() - mountedAt.current,
                streamingNewLatency.current,
                connectionPreparingLatency.current,
                0
              )
            streamRef.current = mediaStream
            playVideo()
            startAvSyncSampling()
          }
        }
      )

      room.on(LivekitClient.RoomEvent.TrackUnsubscribed, () => {
        // Track removal handled by room disconnect
      })

      const prepareStartTime = Date.now()
      await room.prepareConnection(
        sessionInfo.livekit_url,
        sessionInfo.livekit_client_token
      )

      connectionPreparingLatency.current = Date.now() - prepareStartTime
      jsLogger.log('Connection prepared')

      jsLogger.log('Session created successfully')

      return sessionInfo
    } catch (error) {
      jsLogger.error('Error creating new session', {
        error: serializeError(error)
      })
      onError && onError('Network issues detected')
    }
  }, [
    avatarSession,
    playVideo,
    trackInitializationLatencies,
    onError,
    onConnectionStatusChanged,
    onIdleTimeout
  ])

  // Additively publish the user's camera into the server-provisioned room as one
  // adaptive simulcast track, so the server can subscribe to live frames + Egress
  // can record it. Best-effort — any failure here must never break the avatar
  // session. No-op until the room is connected and a live camera track exists.
  const publishUserCamera = (room: LivekitClient.Room): void => {
    if (cameraPublishedRef.current) return
    if (!roomConnectedRef.current) return // publisher transport not ready yet
    const videoTrack = userStream?.getVideoTracks?.()[0]
    // Guard against a missing OR stale track: across back-to-back sessions on the
    // same page the previous stream can be released, leaving an 'ended' track —
    // publishing that silently yields no media. Wait for a LIVE track instead;
    // the effect below re-invokes this when the stream/connection changes.
    if (!videoTrack || videoTrack.readyState !== 'live') {
      jsLogger.warn('[LiveKitMediaPlane] publishUserCamera: no live video track yet', {
        hasStream: !!userStream,
        hasTrack: !!videoTrack,
        readyState: videoTrack?.readyState,
        room: avatarSession.roomName
      })
      return
    }
    cameraPublishedRef.current = true
    publishedCameraTrackRef.current = videoTrack
    jsLogger.log('[LiveKitMediaPlane] publishUserCamera: publishing', {
      room: avatarSession.roomName,
      trackId: videoTrack.id,
      settings: videoTrack.getSettings?.()
    })
    room.localParticipant
      .publishTrack(videoTrack, {
        source: LivekitClient.Track.Source.Camera,
        // Simulcast so the server subscribes to the top layer for analysis
        // quality while weak clients still deliver a usable lower layer.
        simulcast: true,
        videoEncoding: LivekitClient.VideoPresets.h720.encoding,
        videoSimulcastLayers: [
          LivekitClient.VideoPresets.h180,
          LivekitClient.VideoPresets.h360
        ]
      })
      .then((pub) => {
        jsLogger.log('[LiveKitMediaPlane] published user camera to LiveKit', {
          trackSid: pub?.trackSid,
          room: avatarSession.roomName
        })
      })
      .catch((error) => {
        cameraPublishedRef.current = false
        publishedCameraTrackRef.current = null
        jsLogger.error('[LiveKitMediaPlane] publishUserCamera failed', {
          error: serializeError(error)
        })
      })
  }

  // Publish the user microphone into the server-provisioned room so the egress
  // composite carries the user's voice next to the avatar's. Independent of
  // publishUserCamera (own once-guard) — the mic can be live even if the camera
  // track isn't, and vice versa. The STT PCM socket is unaffected; this is a
  // second, recording-only consumer of the same mic. Best-effort.
  const publishUserMic = (room: LivekitClient.Room): void => {
    if (micPublishedRef.current) return
    if (!roomConnectedRef.current) return // publisher transport not ready yet
    const audioTrack = userStream?.getAudioTracks?.()[0]
    if (!audioTrack || audioTrack.readyState !== 'live') {
      jsLogger.warn('[LiveKitMediaPlane] publishUserMic: no live audio track yet', {
        hasStream: !!userStream,
        hasTrack: !!audioTrack,
        readyState: audioTrack?.readyState,
        room: avatarSession.roomName
      })
      return
    }
    micPublishedRef.current = true
    publishedMicTrackRef.current = audioTrack
    jsLogger.log('[LiveKitMediaPlane] publishUserMic: publishing', {
      room: avatarSession.roomName,
      trackId: audioTrack.id
    })
    room.localParticipant
      .publishTrack(audioTrack, {
        source: LivekitClient.Track.Source.Microphone
      })
      .then((pub) => {
        jsLogger.log('[LiveKitMediaPlane] published user mic to LiveKit', {
          trackSid: pub?.trackSid,
          room: avatarSession.roomName
        })
      })
      .catch((error) => {
        micPublishedRef.current = false
        publishedMicTrackRef.current = null
        jsLogger.error('[LiveKitMediaPlane] publishUserMic failed', {
          error: serializeError(error)
        })
      })
  }

  // Retire a publication whose MediaStreamTrack the app has since replaced.
  //
  // The document scan needs the camera to itself, so it stops every track on the
  // app stream — including the ones published here — and hands back a fresh
  // getUserMedia stream when it unmounts. LiveKit keeps the publication alive
  // across that, now bound to a stopped track: the room still advertises a
  // camera, but no frames flow. Nothing recovers on its own, so without this the
  // user tile stays frozen for the REST of the session, not just the scan, and
  // the same goes for the mic.
  //
  // Retires only once a live replacement exists. Mid-scan the stream is null,
  // and unpublishing then would end the recording early rather than leave a gap.
  const retireStalePublications = (room: LivekitClient.Room): void => {
    const retire = (
      kind: 'camera' | 'mic',
      publishedRef: React.MutableRefObject<MediaStreamTrack | null>,
      guardRef: React.MutableRefObject<boolean>,
      liveTrack: MediaStreamTrack | undefined
    ): void => {
      const published = publishedRef.current
      if (!published) return
      if (!liveTrack || liveTrack.readyState !== 'live') return
      if (published === liveTrack) return
      jsLogger.log('[LiveKitMediaPlane] retiring stale publication', {
        kind,
        staleTrackId: published.id,
        staleReadyState: published.readyState,
        liveTrackId: liveTrack.id,
        room: avatarSession.roomName
      })
      // Best-effort: even if the unpublish fails, clearing the guard lets the
      // live track be published, which matters more than a tidy room.
      Promise.resolve(room.localParticipant.unpublishTrack(published)).catch(
        (error) => {
          jsLogger.warn('[LiveKitMediaPlane] unpublish stale track failed', {
            kind,
            error: serializeError(error)
          })
        }
      )
      publishedRef.current = null
      guardRef.current = false
    }
    retire(
      'camera',
      publishedCameraTrackRef,
      cameraPublishedRef,
      userStream?.getVideoTracks?.()[0]
    )
    retire(
      'mic',
      publishedMicTrackRef,
      micPublishedRef,
      userStream?.getAudioTracks?.()[0]
    )
  }

  // Start streaming session
  const startStreamingSession = async () => {
    try {
      const sessionInfo = sessionInfoRef.current
      if (sessionInfo) {
        jsLogger.log('SESSION INFO', { sessionInfo })

        const room = roomRef.current
        if (room) {
          await room.connect(
            sessionInfo.livekit_url,
            sessionInfo.livekit_client_token,
            {
              // Let ICE use the full candidate set (no relay forcing, no ICE
              // server filtering): fast UDP for the common case, automatic
              // TURN/TLS:443 fallback for firewalled clients, all from this one
              // attempt. The generous timeout lets the slow relay fallback
              // complete on the FIRST try; a smaller budget expires on marginal
              // networks and tears the PC down.
              peerConnectionTimeout: 15000
            }
          )
          jsLogger.log('Connected to room')
          jsLogger.log('Streaming started successfully')
          publishUserCamera(room)
          publishUserMic(room)
        }
      }
    } catch (error) {
      jsLogger.error('Error starting streaming session', {
        error: serializeError(error)
      })
    }
  }

  useEffect(() => {
    const init = async () => {
      jsLogger.debug('LiveKitMediaPlane: Initializing', {
        room: avatarSession.roomName
      })
      await createNewSession()
      await startStreamingSession()
    }
    init()

    return () => {
      endSession()
    }
  }, [])

  // Publish the user camera+mic as soon as BOTH the room is connected and a live
  // stream is available — in any order. Re-runs when either the connection flips
  // (roomConnected state) or the stream changes (e.g. a fresh getUserMedia across
  // back-to-back sessions). Both publishers are idempotent (once-guard) and wait
  // for a live track, so repeated invocations are safe.
  useEffect(() => {
    const room = roomRef.current
    if (!room) return
    // Before the once-guards are consulted, drop any publication the app has
    // outgrown — otherwise a post-document-scan stream can never be published.
    retireStalePublications(room)
    if (cameraPublishedRef.current && micPublishedRef.current) return
    publishUserCamera(room)
    publishUserMic(room)
  }, [roomConnected, userStream])

  const endSession = async () => {
    jsLogger.log('End LiveKitMediaPlane session')

    stopAvSyncSampling()
    stopAudioPrime()

    const sessionInfo = sessionInfoRef.current
    if (!sessionInfo) {
      jsLogger.error('No active session')
      return
    }

    jsLogger.log('Leaving room', { session_id: sessionInfo.session_id })

    // The session is owned + stopped server-side (the server holds the LiveAvatar
    // session token); the client only leaves the room.
    const room = roomRef.current
    if (room) {
      room.disconnect(true)
    }

    jsLogger.log('Session closed (server-provisioned; stop is server-side)')
  }

  return (
    <div
      className='h-full w-full flex flex-col justify-between flex-1 relative bg-gray-700'
      onClick={handleVideoClick}
    >
      {avatarImageUrl && (
        <img
          src={avatarImageUrl}
          alt='Avatar'
          className='absolute w-full h-full object-cover left-0 top-0 right-0 bottom-0'
        />
      )}
      <video
        key={'heygen_video'}
        id='heygen_video'
        ref={videoRef}
        className='w-full h-full object-cover absolute left-0 top-0 right-0 bottom-0 bg-gray-700 transform-gpu'
        playsInline
        controls={false}
        autoPlay={false}
        muted={false}
        poster={avatarImageUrl}
        onAbort={() => jsLogger.log('Video: onAbort')}
        onError={(e) =>
          jsLogger.log('Video: onError', { message: get(e, 'message') })
        }
      />
    </div>
  )
}

export default forwardRef(LiveKitMediaPlane)
